import { labelAntrian } from '../utils/antrian';
import { labelPembayaran } from '../utils/labelPembayaran';
import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import {TableContainer} from '@mui/material';
import { Download } from '@mui/icons-material';
import dayjs from 'dayjs';
import useSWR from 'swr';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { exportStyledExcel } from '../utils/exportHelpers';
import { waktuCabang } from '../utils/waktuCabang';
import { apiHost } from '../utils/apiHost';
import RefundDialog from './refund/RefundDialog';
import RefundDiDetail from './refund/RefundDiDetail';
import { notif, konfirmasi } from '../utils/dialog';
import { barisBagi } from '../utils/bagiBayar';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\/+/, '');
  return `${protocol}://${baseUrl}`;
};

const statusMap = {
  'settlement': { label: 'Success', color: 'success' },
  'dibuat': { label: 'Pending', color: 'warning' },
  'pending': { label: 'Pending', color: 'warning' },
  'failure': { label: 'Failed', color: 'error' },
  'deny': { label: 'Failed', color: 'error' },
  // Dibatalkan kasir, QRIS kedaluwarsa, atau dibatalkan pelanggan.
  'cancel': { label: 'Cancel', color: 'error' },
  'expire': { label: 'Cancel', color: 'error' }
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: 1
};

// Status yang ditampilkan: refund penuh tetap 'cancel' di database (tidak dihitung sebagai penjualan), tetapi
// ditampilkan sebagai "Refund"; refund sebagian tetap lunas dengan penanda "Refund sebagian".
// Ungu: beda dari hijau (sukses), merah (cancel), dan oranye (pending).
const REFUND_SX = { bgcolor: '#7b1fa2', color: '#fff' };
const statusTampil = (t) => {
  const refund = Number(t?.totalRefund) > 0;
  if (refund && t.status_pembayaran === 'cancel' && Number(t.totaljual) === 0) return { label: 'Refund', color: 'default', sx: REFUND_SX };
  if (refund && ['settlement', 'capture'].includes(t.status_pembayaran)) return { label: 'Refund sebagian', color: 'default', sx: REFUND_SX };
  return statusMap[t?.status_pembayaran] || { label: 'Unknown', color: 'default' };
};

const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);

export const Transaction = ({ userRole }) => {
  const baseUrl = getApiBaseUrl();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [refundUuid, setRefundUuid] = useState(null);

  const [filters, setFilters] = useState({ username: '', branch: '', date: '' });
  const itemsPerPage = 10;

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page: String(currentPage),
      limit: String(itemsPerPage),
    });
    if (filters.username) params.append('username', filters.username);
    if (filters.date) params.append('tanggal', filters.date);
    return `${baseUrl}/backoffice/transaksi-paginate?${params.toString()}`;
  }, [baseUrl, currentPage, itemsPerPage, filters.username, filters.date]);

  const { data, error, mutate } = useSWR(apiUrl, fetcher);

  if (error) return <div>Error loading transactions.</div>;
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
        <CircularProgress />
      </Box>
    );
  }

  const transactions = data?.data || [];

  // Filter transactions
  const filteredTransactions = transactions.filter((transaction) => {
    const matchesUsername = filters.username
      ? transaction.User?.username?.toLowerCase().includes(filters.username.toLowerCase())
      : true;
    const matchesBranch = userRole === 'superadmin' && filters.branch
      ? transaction.User?.Cabang?.namacabang?.toLowerCase().includes(filters.branch.toLowerCase())
      : true;
    const matchesDate = filters.date
      ? dayjs(transaction.tanggal).isSame(filters.date, 'day')
      : true;

    return matchesUsername && matchesBranch && matchesDate;
  });

  const totalPages = data?.totalPages || 1;

  // Export PDF Function
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Transaction Report', pageWidth / 2, 20, { align: 'center' });
    
    // Date range info
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const reportDate = `Report Generated: ${dayjs().format('DD MMM YYYY HH:mm:ss')}`;
    doc.text(reportDate, pageWidth / 2, 30, { align: 'center' });
    
    // Filters info
    let filterInfo = [];
    if (filters.username) filterInfo.push(`Username: ${filters.username}`);
    if (filters.branch && userRole === 'superadmin') filterInfo.push(`Branch: ${filters.branch}`);
    if (filters.date) filterInfo.push(`Date: ${dayjs(filters.date).format('DD MMM YYYY')}`);
    
    if (filterInfo.length > 0) {
      doc.text(`Filters: ${filterInfo.join(', ')}`, 20, 40);
    }

    // Prepare table data
    const tableHeaders = ['Transaction ID', 'Date', 'Total Amount', 'Payment', 'Status', 'Kasir'];
    if (userRole === 'superadmin') {
      tableHeaders.splice(5, 0, 'Branch');
    }

    const tableData = filteredTransactions.map((transaction) => {
      const row = [
        transaction.uuid,
        dayjs(transaction.tanggal).format('DD MMM YYYY'),
        new Intl.NumberFormat('id-ID', {
          style: 'currency',
          currency: 'IDR'
        }).format(transaction.totaljual),
        transaction.pembayaran,
        statusTampil(transaction).label,
        transaction.User?.username || 'N/A'
      ];
      
      if (userRole === 'superadmin') {
        row.splice(5, 0, transaction.User?.Cabang?.namacabang || 'No Branch');
      }
      
      return row;
    });

    // Add table to PDF
    doc.autoTable({
      head: [tableHeaders],
      body: tableData,
      startY: filterInfo.length > 0 ? 50 : 40,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 30 }, // Transaction ID
        1: { cellWidth: 25 }, // Date
        2: { cellWidth: 30 }, // Amount
        3: { cellWidth: 20 }, // Payment
        4: { cellWidth: 20 }, // Status
        5: userRole === 'superadmin' ? { cellWidth: 25 } : { cellWidth: 20 }, // Branch/Kasir
        6: userRole === 'superadmin' ? { cellWidth: 20 } : undefined, // Kasir for superadmin
      },
      margin: { top: 10 },
      didDrawPage: (data) => {
        // Footer
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber}`,
          pageWidth - 20,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'right' }
        );
      },
    });

    // Summary section
   const totalAmount = filteredTransactions.reduce((sum, transaction) => {
    const totaljualAsNumber = parseFloat(transaction.totaljual) || 0;
    return sum + totaljualAsNumber;
}, 0);
    const successCount = filteredTransactions.filter(t => t.status_pembayaran === 'settlement').length;
    const pendingCount = filteredTransactions.filter(t => ['pending', 'dibuat'].includes(t.status_pembayaran)).length;
    const failedCount = filteredTransactions.filter(t => ['failure', 'cancel'].includes(t.status_pembayaran)).length;

    const finalY = doc.lastAutoTable.finalY + 10;
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', 20, finalY);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Total Transactions: ${filteredTransactions.length}`, 20, finalY + 10);
    doc.text(`Total Amount: ${new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(totalAmount)}`, 20, finalY + 20);
    doc.text(`Success: ${successCount} | Pending: ${pendingCount} | Failed: ${failedCount}`, 20, finalY + 30);

    // Save PDF
    const fileName = `transactions_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;
    doc.save(fileName);
  };

  const exportToExcel = async () => {
    const headers = ['Transaction ID', 'Date', 'Total Amount', 'Payment', 'Status', 'Kasir'];
    if (userRole === 'superadmin') headers.splice(5, 0, 'Branch');

    const rows = filteredTransactions.map((transaction) => {
      const row = [
        transaction.uuid,
        dayjs(transaction.tanggal).format('DD MMM YYYY'),
        new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(transaction.totaljual),
        transaction.pembayaran,
        statusTampil(transaction).label,
        transaction.User?.username || 'N/A'
      ];
      if (userRole === 'superadmin') {
        row.splice(5, 0, transaction.User?.Cabang?.namacabang || 'No Branch');
      }
      return row;
    });

    const totalAmount = filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.totaljual) || 0), 0);
    const successCount = filteredTransactions.filter(t => t.status_pembayaran === 'settlement').length;
    const pendingCount = filteredTransactions.filter(t => ['pending', 'dibuat'].includes(t.status_pembayaran)).length;
    const failedCount = filteredTransactions.filter(t => ['deny', 'cancel', 'expire', 'failure'].includes(t.status_pembayaran)).length;

    await exportStyledExcel({
      fileName: `transactions_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`,
      sheetName: 'Transactions',
      title: 'Transaction Report',
      metaRows: [
        ['Total Transactions', filteredTransactions.length],
        ['Total Amount', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(totalAmount)],
        ['Success / Pending / Failed', `${successCount} / ${pendingCount} / ${failedCount}`],
      ],
      headers,
      rows,
    });
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1);
    setFilters({ ...filters, [name]: value });
  };

  const updateTransaction = async (transactionId, updatedData) => {
    try {
      const response = await axios.put(`${getApiBaseUrl()}/updatetransaksi/${transactionId}`, updatedData, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to update transaction:", error);
      throw error;
    }
  };
  
  const deleteTransaction = async (transactionId) => {
    try {
      const response = await axios.delete(`${getApiBaseUrl()}/deletetransaksi/${transactionId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to delete transaction:", error);
      throw error;
    }
  };

  const cancelTransaction = async (transactionId) => {
    try {
      const response = await axios.patch(`${getApiBaseUrl()}/canceltransaksi/${transactionId}`, {}, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to cancel transaction:", error);
      throw error;
    }
  };

  const handleEditClick = (transaction) => {
    setSelectedTransaction(null); 
    setModalOpen(false); 
    setEditData({
      uuid: transaction.uuid,
      totaljual: transaction.totaljual,
      pembayaran: transaction.pembayaran,
      status_pembayaran: transaction.status_pembayaran,
    });
    setIsEditing(true); 
  };
  
  const handleSaveEdit = async () => {
    try {
      await updateTransaction(editData.uuid, editData);
      setIsEditing(false);
      notif.sukses("Transaksi berhasil diperbarui.");
    } catch (e) {
      notif.error(e?.response?.data?.message || "Gagal memperbarui transaksi.");
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    const confirmDelete = await konfirmasi("Hapus transaksi ini secara permanen?");
    if (confirmDelete) {
      try {
        await deleteTransaction(transactionId);
        notif.sukses("Transaksi berhasil dihapus.");
      } catch (e) {
        notif.error(e?.response?.data?.message || "Gagal menghapus transaksi.");
      }
    }
  };

  const handleCancelTransaction = async (transactionId) => {
    const confirmCancel = await konfirmasi("Batalkan transaksi ini? Serial number/stok akan dikembalikan.");
    if (confirmCancel) {
      try {
        await cancelTransaction(transactionId);
        await mutate();
        notif.sukses("Transaksi berhasil dibatalkan.");
      } catch (e) {
        notif.error(e?.response?.data?.message || "Gagal membatalkan transaksi.");
      }
    }
  };

  const handleRowClick = (event, transaction) => {
    // Prevent row click if clicking on the action buttons
    if (event.target.closest('button')) {
      return;
    }
    setSelectedTransaction(transaction);
    setModalOpen(true);
  };
  
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Card>
        <CardHeader 
          title={userRole === 'superadmin' ? 'All Transactions' : 'Branch Transactions'} 
          subheader={`Total transactions: ${data?.totalData || 0}`}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={exportToPDF}
                color="primary"
                disabled={filteredTransactions.length === 0}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={exportToExcel}
                color="success"
                disabled={filteredTransactions.length === 0}
              >
                Export Excel
              </Button>
            </Box>
          }
        />
        <Divider />

        {/* Filter Inputs */}
        <Box sx={{ display: 'flex', gap: 2, padding: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Username"
            name="username"
            value={filters.username}
            onChange={handleFilterChange}
            size="small"
          />
          {userRole === 'superadmin' && (
            <TextField
              label="Branch"
              name="branch"
              value={filters.branch}
              onChange={handleFilterChange}
              size="small"
            />
          )}
          <TextField
            label="Date"
            name="date"
            type="date"
            value={filters.date}
            onChange={handleFilterChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            onClick={() => setFilters({ username: '', branch: '', date: '' })}
            size="small"
          >
            Clear Filters
          </Button>
        </Box>

        <Divider />

        {/* Transactions Table */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 200 }}>
            <TableHead>
              <TableRow>
                <TableCell>Transaction ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Status</TableCell>
                {userRole === 'superadmin' && <TableCell>Branch Name</TableCell>}
                <TableCell>Kasir</TableCell>
                {/* <TableCell></TableCell> */}
                <TableCell>CreatedAt</TableCell>
                
                {["superadmin", "admin"].includes(userRole) && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.map((transaction) => {
                const statusConfig = statusTampil(transaction);

                return (
                  <TableRow 
                    hover 
                    key={transaction.uuid}
                    onClick={(e) => handleRowClick(e, transaction)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{transaction.uuid}</TableCell>
                    <TableCell>{dayjs(transaction.tanggal).format('DD MMM YYYY')}</TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('id-ID', {
                        style: 'currency',
                        currency: 'IDR'
                      }).format(transaction.totaljual)}
                      {Number(transaction.totalRefund) > 0 && (
                        <Typography variant="caption" color="warning.dark" display="block">
                          Refund {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(transaction.totalRefund)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{labelPembayaran(transaction)}</TableCell>
                    <TableCell>
                      <Chip color={statusConfig.color} label={statusConfig.label} size="small" sx={statusConfig.sx} />
                    </TableCell>
                    {userRole === 'superadmin' && (
                      <TableCell>
                        {transaction.User?.Cabang?.namacabang || 'No Branch'}
                      </TableCell>
                    )}
                    <TableCell>{transaction.User?.username || 'N/A'}</TableCell>
                    <TableCell>
                      {transaction.createdAt
                        ? dayjs(waktuCabang(transaction.createdAt, transaction.User?.Cabang?.zona_waktu)).format('HH:mm:ss')
                        : 'N/A'}
                      {/* Transaksi offline yang jam HP-nya berubah saat offline: jam dikoreksi otomatis. */}
                      {transaction.jam_perlu_dicek && (
                        <Chip
                          size="small"
                          color="warning"
                          label="Jam perlu dicek"
                          title="Transaksi offline: jam HP kasir berubah saat offline, jam dikoreksi otomatis oleh server."
                          sx={{ ml: 1, height: 20, fontSize: 11 }}
                        />
                      )}
                    </TableCell>
                    {["superadmin", "admin"].includes(userRole) && (
                <TableCell>
                  {['settlement', 'capture'].includes(transaction.status_pembayaran) && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setRefundUuid(transaction.uuid)}
                      sx={{ mr: 1 }}
                    >
                      Refund
                    </Button>
                  )}
                  {!['cancel', 'deny', 'expire'].includes(transaction.status_pembayaran) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => handleCancelTransaction(transaction.uuid)}
                      sx={{ mr: 1 }}
                    >
                      Cancel
                    </Button>
                  )}
                  {userRole === "superadmin" && (
                    <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleEditClick(transaction)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleDeleteTransaction(transaction.uuid)}
                  >
                    Delete
                  </Button>
                  </>
                  )}
                </TableCell>
              )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Divider />

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => setCurrentPage(value)}
            color="primary"
          />
        </Box>
      </Card>

      <RefundDialog
        transaksiUuid={refundUuid}
        open={Boolean(refundUuid)}
        onClose={() => setRefundUuid(null)}
        onDone={(pesan) => { mutate(); notif.sukses(pesan); }}
      />

      {/* Transaction Detail Modal */}
      <Modal
         open={modalOpen && !isEditing}
        onClose={handleCloseModal}
        aria-labelledby="transaction-detail-modal"
      >
        <Box sx={modalStyle}>
          {selectedTransaction && (
            <>
              <Typography variant="h6" component="h2" gutterBottom>
                Transaction Details
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Transaction Information
                </Typography>
                <Paper sx={{ p: 2 }}>
                  <Typography><strong>Transaction ID       :</strong> {selectedTransaction.uuid}</Typography>
                  <Typography><strong>Order ID             :</strong> {selectedTransaction.order_id}</Typography>
                  {labelAntrian(selectedTransaction) && (
                    <Typography><strong>No. Antrian          :</strong> {labelAntrian(selectedTransaction)}</Typography>
                  )}
                  <Typography><strong>Date                 :</strong> {dayjs(selectedTransaction.tanggal).format('DD MMM YYYY')}</Typography>
                  <Typography><strong>Created At           :</strong> {dayjs(waktuCabang(selectedTransaction.createdAt, selectedTransaction.User?.Cabang?.zona_waktu)).format('DD MMM YYYY HH:mm:ss')}</Typography>
                  <Typography><strong>Payment Method       :</strong> {labelPembayaran(selectedTransaction)}</Typography>
                  {Array.isArray(selectedTransaction.pembayaranBagi) && selectedTransaction.pembayaranBagi.length > 0 && (
                    barisBagi(selectedTransaction.pembayaranBagi, parseFloat(selectedTransaction.totaljual) || 0).map((l, i) => (
                      <Typography key={i} variant="body2" sx={{ pl: 2 }}>• {l}</Typography>
                    ))
                  )}
                  {!selectedTransaction.bagiBayar && selectedTransaction.pembayaran === 'noncash' && (
                    <Typography><strong>Non Cash Method      :</strong> {selectedTransaction.nonCashMethodName || 'N/A'}</Typography>
                  )}
                  <Typography><strong>Status               :</strong> {statusTampil(selectedTransaction).label}</Typography>
                  <Typography><strong>Customer             :</strong> {selectedTransaction.namaCustomer || 'N/A'}</Typography>
                  <Typography><strong>Noted                :</strong> {selectedTransaction.noted || 'N/A'}</Typography>
                  <Typography><strong>Table                :</strong> {selectedTransaction.Table?.name || 'N/A'}</Typography>
                  <Typography><strong>Status Order         :</strong> {selectedTransaction.status_order || 'N/A'}</Typography>
                  <Typography><strong>Metode Pesanan       :</strong> {selectedTransaction.metode_pesanan || 'N/A'}</Typography>
                  {selectedTransaction.metode_pesanan === 'takeaway' && (
                    <Typography><strong>Take Away Method     :</strong> {selectedTransaction.takeawayName || 'N/A'}</Typography>
                  )}

                  {(() => {
                    const fmt = (v) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(v || 0);
                    const itemsSubtotal = (selectedTransaction.TransaksiDetails || [])
                      .reduce((sum, i) => sum + parseFloat(i.total || 0), 0);
                    const subtotal = selectedTransaction.subtotal != null ? parseFloat(selectedTransaction.subtotal) : itemsSubtotal;
                    const ppnNominal = selectedTransaction.ppnNominal != null
                      ? parseFloat(selectedTransaction.ppnNominal)
                      : parseFloat(selectedTransaction.totaljual) - subtotal;
                    return (
                      <>
                        <Typography><strong>Subtotal (Items)     :</strong> {fmt(subtotal)}</Typography>
                        {ppnNominal > 0 && (
                          <Typography><strong>PPN{selectedTransaction.ppnPersen ? ` ${selectedTransaction.ppnPersen}%` : ''}             :</strong> {fmt(ppnNominal)}</Typography>
                        )}
                        <Typography><strong>Total Amount         :</strong> {fmt(selectedTransaction.totaljual)}</Typography>
                        {ppnNominal > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Total Amount = Subtotal Items + PPN ({fmt(subtotal)} + {fmt(ppnNominal)} = {fmt(selectedTransaction.totaljual)})
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                  <Typography><strong>Uang Customer         :</strong> {
                    new Intl.NumberFormat('id-ID', {
                      style: 'currency',
                      currency: 'IDR'
                    }).format(selectedTransaction.uangCustomer || 'Qris')
                  }</Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Cashier Information
                </Typography>
                <Paper sx={{ p: 2 }}>
                  <Typography><strong>Cashier:</strong> {selectedTransaction.User?.username}</Typography>
                  <Typography><strong>Branch:</strong> {selectedTransaction.User?.Cabang?.namacabang}</Typography>
                </Paper>
              </Box>

              {Number(selectedTransaction.totalRefund) > 0 && <RefundDiDetail transaksiUuid={selectedTransaction.uuid} />}

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Items
                </Typography>
                <Paper sx={{ p: 2 }}>
                  <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Addon</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Harga Satuan</TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell>Catatan</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTransaction.TransaksiDetails.map((item) => {
                          const formatRupiah = (v) => new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR'
                          }).format(v);
                          const hargaDasar = parseFloat(item.Barang?.harga ?? item.harga);
                          const totalTambahan = (item.TransaksiDetailOpsis || [])
                            .reduce((sum, o) => sum + parseFloat(o.hargatambahan || 0), 0);
                          return (
                            <TableRow key={item.uuid}>
                              <TableCell>{item.Barang?.namabarang || 'NA'}</TableCell>
                              <TableCell>
                                {(item.TransaksiDetailOpsis || []).length > 0
                                  ? item.TransaksiDetailOpsis.map((o) => {
                                      const tambahan = parseFloat(o.hargatambahan || 0);
                                      return `${o.opsinama}${tambahan > 0 ? ` (+${formatRupiah(tambahan)})` : ''}`;
                                    }).join(', ')
                                  : '-'}
                              </TableCell>
                              <TableCell>{item.jumlahbarang}</TableCell>
                              <TableCell>
                                {formatRupiah(item.harga)}
                                {totalTambahan > 0 && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    Dasar {formatRupiah(hargaDasar)} + addon {formatRupiah(totalTambahan)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{formatRupiah(item.total)}</TableCell>
                              <TableCell>{item.catatan || '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleCloseModal} variant="contained">
                  Close
                </Button>
              </Box>
            </>
          )}
         
        </Box>
      </Modal>

      {/* Modal Edit */}
      <Modal open={isEditing} onClose={() => setIsEditing(false)}>
         <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom>
          Edit Transaction
        </Typography>
        <TextField
          label="Total Amount"
          type="number"
          fullWidth
          margin="normal"
          value={editData.totaljual}
          onChange={(e) => setEditData({ ...editData, totaljual: e.target.value })}
        />
        <TextField
          label="Payment Method"
          fullWidth
          margin="normal"
          value={editData.pembayaran}
          onChange={(e) => setEditData({ ...editData, pembayaran: e.target.value })}
        />
        <TextField
          label="Payment Status"
          fullWidth
          margin="normal"
          value={editData.status_pembayaran}
          onChange={(e) => setEditData({ ...editData, status_pembayaran: e.target.value })}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="contained" onClick={handleSaveEdit}>
            Save
          </Button>
          <Button variant="outlined" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
    </Box>
  );
};